import { primary } from "@/theme/colors";
import { Box, Typography } from "@mui/material";
import { FC } from "react";

type TProgressDonutChartProps = {
  percent: number;
  size?: number;
};

const GREEN = "#4CAF50";
const GREEN_BG = "#E8F5E9";
const ORANGE = "#FF9800";
const ORANGE_BG = "#FFF3E0";
const RED = "#F44336";
const RED_BG = "#FFEBEE";

const getColorByPercent = (pct: number) => {
  if (pct >= 100) return { stroke: GREEN, track: GREEN_BG };
  if (pct >= 50) return { stroke: ORANGE, track: ORANGE_BG };
  return { stroke: RED, track: RED_BG };
};

export const ProgressDonutChart: FC<TProgressDonutChartProps> = ({
  percent,
  size = 168,
}) => {
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedPercent = Math.min(percent, 100);
  const offset = circumference - (clampedPercent / 100) * circumference;
  const center = size / 2;
  const { stroke, track } = getColorByPercent(percent);

  const getMessage = (pct: number) => {
    if (pct >= 100) return "目標達成！";
    if (pct >= 50) return "あと一息！";
    return "頑張っていこう！";
  };

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={track}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <Box
        sx={{
          // Slight offset to optically center the text within the ring (matches webview)
          position: "absolute",
          top: size * (44 / 164),
          left: size * (24 / 164),
          width: size * (120 / 164),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body14Semi" color={primary[400]} textAlign="center">
          {getMessage(percent)}
        </Typography>
        <Typography variant="head32Bold" color={stroke}>
          {clampedPercent}%
        </Typography>
      </Box>
    </Box>
  );
};
